<template>
  <section class="events-section">
    <div class="container">
      <div class="text-center mb-5">
        <h2 class="events-section-title">Events</h2>
        <p class="events-section-subtitle">Join us for exciting franchise events and networking opportunities</p>
      </div>
      
      <!-- Loading State -->
      <div v-if="eventStore.loading" class="text-center py-5">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p class="loading-text">Loading events...</p>
        </div>
      </div>
      
      <!-- Error State -->
      <div v-else-if="eventStore.error" class="text-center py-5">
        <div class="error-container">
          <i class="fa-solid fa-exclamation-triangle error-icon"></i>
          <h4 class="error-title">Unable to load events</h4>
          <p class="error-message">{{ eventStore.error }}</p>
          <button @click="loadEvents" class="retry-btn">
            <i class="fa-solid fa-refresh"></i>
            Try Again
          </button>
        </div>
      </div>
      
      <!-- Events Grid -->
      <div v-else-if="latestEvents.length > 0">
        <div class="row">
          <div v-for="event in latestEvents" :key="event.id" class="col-lg-4 col-md-6 mb-4">
            <EventCard :event="event" />
          </div>
        </div>
        <div class="text-center mt-5">
          <router-link to="/events" class="view-all-btn">
            <span>View All Events</span>
            <i class="fa-solid fa-arrow-right"></i>
          </router-link>
        </div>
      </div>
      
      <!-- No Events State -->
      <div v-else class="text-center py-5">
        <div class="no-events-container">
          <i class="fa-solid fa-calendar-xmark no-events-icon"></i>
          <h4 class="no-events-title">No events available</h4>
          <p class="no-events-message">Check back soon for upcoming events.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useEventStore } from '../stores/eventStore'
import EventCard from './EventCard.vue'

const eventStore = useEventStore()

const latestEvents = computed(() => {
  // Sort by date (newest first), take 6
  return [...eventStore.activeEvents].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6)
})

const loadEvents = async () => {
  await eventStore.fetchEvents()
}

onMounted(() => {
  if (eventStore.events.length === 0) {
    loadEvents()
  }
})
</script>

<style scoped>
.events-section {
  padding: 5rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}

.events-section-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: #000000;
  margin-bottom: 1rem;
  letter-spacing: -0.5px;
}

.events-section-subtitle {
  font-size: 1.3rem;
  color: #6c757d;
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Loading State */
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid var(--primary-orange, #ff6b35);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 1.1rem;
  color: #6c757d;
  font-weight: 500;
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(44,62,80,0.08);
  max-width: 500px;
  margin: 0 auto;
}

.error-icon {
  font-size: 3rem;
  color: #f59e0b;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.error-message {
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
  text-align: center;
}

.retry-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--primary-orange, #ff6b35);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(255,107,53,0.3);
}

.retry-btn:hover {
  background: #e55a2b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.4);
}

/* View All Button */
.view-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: var(--primary-orange, #ff6b35);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
  font-family: inherit;
}

.view-all-btn:hover {
  background: #e55a2b;
  color: #fff;
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255,107,53,0.4);
}

.view-all-btn i {
  font-size: 1.1rem;
  transition: transform 0.3s ease;
}

.view-all-btn:hover i {
  transform: translateX(4px);
}

/* No Events State */
.no-events-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(44,62,80,0.08);
  max-width: 500px;
  margin: 0 auto;
}

.no-events-icon {
  font-size: 3.5rem;
  color: #d1d5db;
}

.no-events-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
}

.no-events-message {
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

/* Responsive Design */
@media (max-width: 900px) {
  .events-section {
    padding: 4rem 0;
  }
  
  .events-section-title {
    font-size: 2.2rem;
  }
  
  .events-section-subtitle {
    font-size: 1.1rem;
  }
  
  .view-all-btn {
    padding: 14px 28px;
    font-size: 1rem;
  }
}

@media (max-width: 600px) {
  .events-section {
    padding: 3rem 0;
  }
  
  .events-section-title {
    font-size: 1.8rem;
  }
  
  .events-section-subtitle {
    font-size: 1rem;
  }
  
  .error-container,
  .no-events-container {
    margin: 0 1rem;
    padding: 1.5rem;
  }
  
  .view-all-btn {
    padding: 12px 24px;
    font-size: 0.95rem;
  }
}
</style> 